import React, { useMemo } from "react";
import {
  find,
  first,
  includes,
  last,
  map,
  orderBy,
  some,
  toUpper,
} from "lodash";
import { SectionHeader } from "@/shared/ui/SectionHeader";
import LineChart from "@/shared/charts/LineChart";
import { Column, Row } from "@/shared/ui/Blocks";
import { cn } from "@/shared/utils/shadcn";
import { Icon, IconName } from "@/shared/ui/Icon";
import { Dimension } from "@/shared/services/api/graphql/generated";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ModalFileName } from "@/shared/modals";
import { DimensionIcons } from "@/shared/constants";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { DimensionDetails } from "@/shared/widgets/DimensionDetailsSection";
import { DimensionContext } from "@/types/data";
import Chip from "@/shared/ui/Chip";
import { useTranslation } from "react-i18next";

interface PersonalityMapSectionProps {
  dimensions: DimensionWithScores[];
  context?: DimensionContext;
  isPdf?: boolean;
}

function PersonalityMapSection(props: PersonalityMapSectionProps) {
  const { dimensions = [], isPdf = false, context } = props;
  const { t } = useTranslation();
  const [openDimensionModal] = useModal(ModalFileName.DIMENSION_DETAILS);

  const mainDimensions = dimensions?.filter(
    (dimension) => dimension.parent === null
  );

  const handleDimensionClick = (dimension: Dimension) => {
    openDimensionModal({
      dimension,
      allDimensions: dimensions,
      context,
    });
  };

  const columns = useMemo(() => {
    const data = mainDimensions.map((dimension) => ({
      title: dimension.title || "",
      icon: DimensionIcons[dimension.id as keyof typeof DimensionIcons],
      score: dimension.score?.value || 0,
      data: dimension,
      alignment:
        dimension?.peopleScores?.length > 1 ? dimension.alignment : undefined,
    }));
    return data;
  }, [mainDimensions]);

  const comparisonArea = useMemo(
    () =>
      map(mainDimensions, (dimension) => {
        const orderedScores = orderBy(dimension.peopleScores, "value", "desc");

        return {
          high:
            find(dimension.allScores, { key: "max" })?.value ||
            first(orderedScores)?.value ||
            0,
          low:
            find(dimension.allScores, { key: "min" })?.value ||
            last(orderedScores)?.value ||
            0,
        };
      }),
    [mainDimensions]
  );

  const medianLine = useMemo(() => {
    if (context === "user") return undefined;
    return map(
      mainDimensions,
      (dimension) => find(dimension.allScores, { key: "mean" })?.value || 0
    ).filter(Boolean);
  }, [mainDimensions, context]);

  if (dimensions?.length === 0) return null;

  return (
    <section>
      <Column id="personality-map-section">
        <Column id="personality-map-section-title">
          <SectionHeader
            title={t("profile.personality_map")}
            subtitle={t("profile.personality_map_description", {
              context: t(`context_label.${context}_posessive`),
            })}
            className="mb-4"
          />
        </Column>

        <LineChart
          columns={columns}
          comparisonArea={comparisonArea}
          comparisonLine={medianLine}
          onColumnClick={(col) => handleDimensionClick(col.data)}
          height={240}
          fixedWidth={isPdf ? 700 : undefined}
          paddings={{
            bottom: includes(["user"], context) ? 60 : 84,
          }}
          renderColumnFooter={(col, index, isHovered) => (
            <Column
              className={cn(
                "w-full h-full items-center justify-center border-b border-solid border-grey-300 p-1 gap-1",
                index === 0 && "border-l rounded-bl-lg",
                index === columns.length - 1 && "border-r rounded-br-lg"
              )}
            >
              <Row className={cn(" gap-1 transition-all items-center")}>
                <Icon
                  icon={col.icon as IconName}
                  className={cn("transition-all print:text-xs")}
                  size="sm"
                  color="primary"
                />
                <p
                  className={cn(
                    "text-xs leading-3, transition-all print:text-xs",
                    isHovered && "font-semibold text-primary"
                  )}
                >
                  {col.title}
                </p>
              </Row>
              {!!col.alignment?.label && (
                <Chip color={col.alignment?.color} variant="solid" size="sm">
                  <p className="text-white text-xs uppercase font-normal">
                    {toUpper(col.alignment?.label || "")}
                  </p>
                </Chip>
              )}
            </Column>
          )}
        />
      </Column>
      {!isPdf && (
        <Column className="mt-16 gap-16">
          {map(mainDimensions, (dimension) => (
            <DimensionDetails
              key={dimension.id}
              dimension={dimension}
              scores={dimension.peopleScores}
              subDimensions={dimensions.filter(
                (d) => d.parentId === dimension.id
              )}
              extremes={dimension.extremes}
              context={context}
            />
          ))}
        </Column>
      )}
    </section>
  );
}

export { PersonalityMapSection };
